import { stripHtml } from '@shared/services/text'

/**
 * One before → after row in the publish review. `null` means empty or absent;
 * both `null` means the field changed in a way that has no readable value
 * (flow structure, custom code).
 */
export interface FieldChange {
  field: string
  before: string | null
  after: string | null
}

/** Entity ID → readable name, so references render as names instead of IDs. */
export type EntityNames = ReadonlyMap<string, string>

type JsonObject = { [key: string]: Json }
type Json = null | boolean | number | string | Json[] | JsonObject

/** Describes one field whose stored shape has no readable before/after of its own. */
export type FieldDescriber = (before: unknown, after: unknown, names: EntityNames) => FieldChange[]

/**
 * How one kind of entity is worded in the dashboard UI. Keys are dotted config
 * key paths; array items add no segment (`variants.name`, not `variants.0.name`).
 */
export interface FieldVocabulary {
  /** UI label for a key path; `''` folds a wrapper object into its parent's label. */
  labels: Record<string, string>
  /** Key paths the UI never shows (IDs, editor preferences, derived data). */
  hidden?: Record<string, true>
  /** Stored enum value → the word the UI shows for it, per key path. */
  values?: Record<string, Record<string, string>>
  /** Key paths summarized by their own describer instead of field by field. */
  describers?: Record<string, FieldDescriber>
}

// Values that are code: report that they changed, never their contents.
const CODE_KEY = /css|code|script|html|hook/i

// Condition trees and flow graphs have no field-by-field reading; a vocabulary
// describer summarizes them, otherwise the row just says the value changed.
const STRUCTURE_KEYS: Record<string, true> = { conditions: true, definition: true, logic: true, funnelDefinition: true }

// Identity and metadata keys never shown at any depth.
const INTERNAL_KEYS: Record<string, true> = { id: true, _uuid: true, accessedAt: true }

// Entity-level metadata stamps.
const ROOT_METADATA: Record<string, true> = { modifiedAt: true }

// Keys that identify an array item when it has no `id`.
const IDENTITY_KEYS = ['id', 'segmentId', 'indicates'] as const

/** Fallback wording for a key no vocabulary names: `buttonText` → "Button text". */
export function humanizeKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function isJsonObject(value: Json | undefined): value is JsonObject {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function itemKey(item: Json): string | null {
  if (!isJsonObject(item)) {
    return null
  }
  for (const key of IDENTITY_KEYS) {
    const value = item[key]
    if (typeof value === 'string' || typeof value === 'number') {
      return `${key}:${value}`
    }
  }
  return null
}

function itemLabel(item: Json, index: number, names: EntityNames): string {
  if (isJsonObject(item)) {
    for (const key of ['name', 'label', 'title', 'text', 'fallbackText']) {
      const value = item[key]
      if (typeof value === 'string' && stripHtml(value).trim()) {
        return stripHtml(value).trim()
      }
    }
    const id = typeof item.id === 'string' ? item.id : typeof item.segmentId === 'string' ? item.segmentId : null
    const named = id ? names.get(id) : undefined
    if (named) {
      return named
    }
    if (typeof item.type === 'string') {
      return humanizeKey(item.type)
    }
  }
  return `#${index + 1}`
}

function formatValue(value: Json | undefined, names: EntityNames, words?: Record<string, string>): string | null {
  if (value === undefined || value === null) {
    return null
  }
  if (typeof value === 'boolean') {
    return words?.[String(value)] ?? (value ? 'On' : 'Off')
  }
  if (typeof value === 'number') {
    return value.toLocaleString('en-US')
  }
  if (typeof value === 'string') {
    const word = words?.[value] ?? names.get(value)
    if (word) {
      return word
    }
    const text = stripHtml(value).replace(/\s+/g, ' ').trim()
    return text || null
  }
  if (Array.isArray(value)) {
    const items = value.map((item, index) =>
      isJsonObject(item) ? itemLabel(item, index, names) : formatValue(item, names, words)
    )
    const text = items.filter((item): item is string => !!item).join(', ')
    return text || null
  }
  return null
}

/**
 * Matches array items by `id` (with an occurrence suffix for repeated IDs) so
 * an insertion or removal never shifts every later item into a false edit.
 */
function keyedItems(items: Json[]): Map<string, { item: Json; index: number }> {
  const keyed = new Map<string, { item: Json; index: number }>()
  const seen = new Map<string, number>()
  items.forEach((item, index) => {
    const id = itemKey(item) ?? `#${index}`
    const occurrence = seen.get(id) ?? 0
    seen.set(id, occurrence + 1)
    keyed.set(occurrence ? `${id}~${occurrence}` : id, { item, index })
  })
  return keyed
}

/**
 * Readable before → after rows for one entity, worded like the dashboard UI.
 * Arrays of identified objects are compared by ID: additions, removals and
 * reorders are their own rows, matched items are compared field by field.
 */
export function describeFieldChanges(
  before: unknown,
  after: unknown,
  names: EntityNames,
  vocabulary: FieldVocabulary
): FieldChange[] {
  const rows: FieldChange[] = []
  const label = (labels: string[]) => labels.filter(Boolean).join(' › ') || 'Value'

  const walk = (was: Json | undefined, now: Json | undefined, labels: string[], keyPath: string) => {
    if (JSON.stringify(was) === JSON.stringify(now)) {
      return
    }
    const describe = vocabulary.describers?.[keyPath]
    if (describe) {
      rows.push(
        ...describe(was, now, names).map((row) => ({ ...row, field: label([...labels.slice(0, -1), row.field]) }))
      )
      return
    }
    const key = keyPath.split('.').at(-1) ?? ''
    if (CODE_KEY.test(key) || STRUCTURE_KEYS[key]) {
      rows.push({ field: label(labels), before: null, after: null })
      return
    }
    if ((isJsonObject(was) || was === undefined) && (isJsonObject(now) || now === undefined) && (was || now)) {
      const keys = new Set([...Object.keys(was ?? {}), ...Object.keys(now ?? {})])
      for (const child of keys) {
        const childPath = keyPath ? `${keyPath}.${child}` : child
        if (INTERNAL_KEYS[child] || (!keyPath && ROOT_METADATA[child]) || vocabulary.hidden?.[childPath]) {
          continue
        }
        walk(
          isJsonObject(was) ? was[child] : undefined,
          isJsonObject(now) ? now[child] : undefined,
          [...labels, vocabulary.labels[childPath] ?? names.get(child) ?? humanizeKey(child)],
          childPath
        )
      }
      return
    }
    if (Array.isArray(was) && Array.isArray(now) && [...was, ...now].some(isJsonObject)) {
      const previous = keyedItems(was)
      const current = keyedItems(now)
      for (const [id, { item, index }] of previous) {
        if (!current.has(id)) {
          rows.push({ field: label(labels), before: itemLabel(item, index, names), after: null })
        }
      }
      for (const [id, { item, index }] of current) {
        const match = previous.get(id)
        if (!match) {
          rows.push({ field: label(labels), before: null, after: itemLabel(item, index, names) })
        } else {
          walk(match.item, item, [...labels, itemLabel(item, index, names)], keyPath)
        }
      }
      const kept = [...current.keys()].filter((id) => previous.has(id))
      const keptBefore = [...previous.keys()].filter((id) => current.has(id))
      if (kept.join('\n') !== keptBefore.join('\n')) {
        rows.push({
          field: `${label(labels)} order`,
          before: keptBefore.map((id) => itemLabel(previous.get(id)?.item ?? null, 0, names)).join(', '),
          after: kept.map((id) => itemLabel(current.get(id)?.item ?? null, 0, names)).join(', ')
        })
      }
      return
    }
    const words = vocabulary.values?.[keyPath]
    const formattedBefore = formatValue(was, names, words)
    const formattedAfter = formatValue(now, names, words)
    if (formattedBefore !== formattedAfter) {
      rows.push({ field: label(labels), before: formattedBefore, after: formattedAfter })
    } else if (typeof was === 'string' && typeof now === 'string' && formattedBefore !== null) {
      // Same visible text, different markup (bold, a link): still a change to this field.
      rows.push({ field: label(labels), before: null, after: null })
    }
  }

  walk(before as Json, after as Json, [], '')
  return rows
}
